import { useCallback, useEffect, useMemo, useState } from 'react'

import { buildConstructorSnippet } from '@/core/components/Card/components/ConstructorCardContent/utils/buildConstructorSnippet'
import { buildFieldSnippet } from '@/core/components/Card/components/FieldCardContent/utils/buildFieldSnippet'
import { buildMethodConstructors } from '@/core/components/Card/components/MethodCardContent/utils/buildMethodConstructors'
import { UnifiedGraftModelTypeKey } from '@/core/const/unifiedGraftModelTypeKeys.const'
import { buildExecutionCopyText } from '@/core/hooks/utils/buildExecutionCopyText'
import { buildInstanceExecutionParams } from '@/core/hooks/utils/buildInstanceExecutionParams'
import { coerceParametersFromMap } from '@/core/hooks/utils/coerceParametersFromMap'
import { isStaticMethodType } from '@/core/hooks/utils/isStaticMethodType'
import { useCodeStore } from '@/core/stores/codeStore'
import { useLibraryStore } from '@/core/stores/librariesStore'
import type { ParameterData } from '@/core/types/library.type'
import { buildCodeSnippet } from '@/core/utils/builder/codeSnippet.builder'
import { buildVariableCode } from '@/core/utils/builder/variableCode.builder'
import { coerceParameterValue } from '@/core/utils/coerceParameterValue'
import { resolveSnippetTechnology } from '@/core/utils/installation'

import type { Member, UseMemberExecutionParams, UseMemberExecutionResult } from './types'

const EMPTY_VALUE_MAP: Record<string, string | number> = {}

const getResultKey = (member: Member): string => {
  if (member.kind === 'field') return `field-${member.field.name}`
  if (member.kind === 'method') return member.methodKey || member.method.name
  return member.constructorKey ?? member.constructor.name
}

const getMemberParameters = (member: Member): ParameterData[] => {
  if (member.kind === 'field') return []
  if (member.kind === 'method') return member.method.parameters
  return member.constructor.parameters
}

export function useMemberExecution({
  member,
  classData,
  runtimeName,
}: UseMemberExecutionParams): UseMemberExecutionResult {
  const [valueMap, setValueMap] = useState<Record<string, string | number>>(EMPTY_VALUE_MAP)
  const [isTryItMode, setIsTryItMode] = useState(false)
  const [selectedConstructorId, setSelectedConstructorId] = useState<string>('')
  const [constructorValueMap, setConstructorValueMap] = useState<Record<string, string | number>>(EMPTY_VALUE_MAP)

  const { results, logs, executeMember, clearResult } = useLibraryStore()
  const selectedTechnology = useCodeStore((s) => s.selectedTechnology)
  const technologyInstances = useCodeStore((s) => s.technologyInstances)
  const technologyUsages = useCodeStore((s) => s.technologyUsages)
  const technologyVariables = useCodeStore((s) => s.technologyVariables)
  const technologyFieldUsages = useCodeStore((s) => s.technologyFieldUsages)

  const snippetTechnology = useMemo(
    () => resolveSnippetTechnology(selectedTechnology, runtimeName),
    [selectedTechnology, runtimeName]
  )

  const instanceCode = snippetTechnology ? (technologyInstances[snippetTechnology] ?? '') : ''
  const usageCode = snippetTechnology ? (technologyUsages[snippetTechnology] ?? '') : ''
  const variableCode = snippetTechnology ? (technologyVariables[snippetTechnology] ?? '') : ''
  const resultKey = getResultKey(member)
  const memberParameters = getMemberParameters(member)

  const isStatic = useMemo((): boolean => {
    if (member.kind === 'field') {
      return Boolean(member.field.isStatic) || member.field.type === UnifiedGraftModelTypeKey.STATIC_FIELD
    }
    if (member.kind === 'method') return isStaticMethodType(member.method.type)
    return false
  }, [member])

  const isGlobal = useMemo((): boolean => {
    if (member.kind === 'field') return member.field.type === UnifiedGraftModelTypeKey.GLOBAL_FIELD
    if (member.kind === 'method')
      return ['GLOBAL_METHOD', 'OVERLOADED_GLOBAL_METHOD'].includes(String(member.method.type))
    return false
  }, [member])

  const needsInstance = !isStatic && !isGlobal && member.kind !== 'constructor'
  const constructorsAvailable = !!classData.constructors && !!instanceCode && !!variableCode
  const useConstructors = needsInstance && constructorsAvailable

  const constructors = useMemo(
    () =>
      useConstructors
        ? buildMethodConstructors({
            classData,
            instanceCode: instanceCode ?? '',
            variableCode: variableCode ?? '',
            technology: snippetTechnology,
          })
        : undefined,
    [useConstructors, classData, instanceCode, variableCode, snippetTechnology]
  )

  const shouldShowConstructorSelector = useMemo((): boolean => {
    if (!constructors?.length) return false
    if (constructors.length > 1) return true
    return (constructors[0]?.parameters?.length ?? 0) > 0
  }, [constructors])

  useEffect(() => {
    if (!useConstructors || !classData.constructors) return

    const [firstConstructorId] = Object.keys(classData.constructors)

    if (
      !firstConstructorId ||
      selectedConstructorId === firstConstructorId ||
      classData.constructors[selectedConstructorId]
    ) {
      return
    }

    setSelectedConstructorId(firstConstructorId)
    setConstructorValueMap(EMPTY_VALUE_MAP)
  }, [classData.constructors, selectedConstructorId, useConstructors])

  const code = useMemo((): string => {
    const fieldUsageCode = snippetTechnology
      ? (technologyFieldUsages[snippetTechnology] ?? { static: '', instance: '', global: '' })
      : { static: '', instance: '', global: '' }

    if (member.kind === 'field') {
      return buildFieldSnippet({
        classData,
        field: member.field,
        fieldUsageCode,
        instanceCode,
        useConstructors: Boolean(constructors?.length),
        technology: snippetTechnology,
      })
    }

    if (member.kind === 'method') {
      return buildCodeSnippet(
        member.method,
        classData,
        isStatic,
        useConstructors,
        instanceCode,
        usageCode,
        snippetTechnology
      )
    }

    return buildConstructorSnippet({
      classData,
      constructor: member.constructor,
      instanceCode,
      technology: snippetTechnology,
    })
  }, [
    classData,
    constructors,
    instanceCode,
    isStatic,
    member,
    snippetTechnology,
    technologyFieldUsages,
    usageCode,
    useConstructors,
  ])

  const allVariableCode = useMemo(
    () => buildVariableCode(memberParameters, variableCode, valueMap, snippetTechnology ?? ''),
    [memberParameters, variableCode, valueMap, snippetTechnology]
  )

  const executionLogs = useMemo((): string[] => logs?.[resultKey] ?? [], [logs, resultKey])
  const result = results?.[resultKey] ?? ''

  const handleValueChange = useCallback((parameter: ParameterData, value: string | number) => {
    setValueMap((prev) => ({ ...prev, [parameter.name]: value }))
  }, [])

  const handleTryIt = useCallback(() => {
    setIsTryItMode((prev) => {
      if (prev) clearResult(resultKey)
      return !prev
    })
  }, [clearResult, resultKey])

  const handleCopy = useCallback(() => {
    const text = buildExecutionCopyText({ isTryItMode, allVariableCode, code })
    navigator.clipboard.writeText(text)
  }, [code, isTryItMode, allVariableCode])

  const handleRun = useCallback(() => {
    const { instanceParams, constructorKey } = buildInstanceExecutionParams({
      isStatic: !needsInstance,
      classData,
      selectedConstructorId,
      constructorValueMap,
    })

    if (member.kind === 'field') {
      executeMember({
        runtimeName,
        classData,
        member: { kind: 'field', field: member.field },
        resultKey,
        instanceParams,
        constructorKey,
      })
      return
    }

    if (member.kind === 'method') {
      const methodParams = coerceParametersFromMap({ parameters: member.method.parameters, valueMap })
      executeMember({
        runtimeName,
        classData,
        member: { kind: 'method', method: member.method, methodKey: member.methodKey, isStatic },
        resultKey,
        methodParams,
        instanceParams,
        constructorKey,
      })
      return
    }

    const ctorInstanceParams = member.constructor.parameters.map((parameter) =>
      coerceParameterValue(valueMap[parameter.name] ?? '', parameter.typeUsage ?? parameter.primitiveType)
    )

    executeMember({
      runtimeName,
      classData,
      member: { kind: 'constructor' },
      resultKey,
      instanceParams: ctorInstanceParams,
      constructorKey: resultKey,
    })
  }, [
    classData,
    constructorValueMap,
    executeMember,
    isStatic,
    member,
    needsInstance,
    resultKey,
    runtimeName,
    selectedConstructorId,
    valueMap,
  ])

  const handleClear = useCallback(() => {
    setValueMap(EMPTY_VALUE_MAP)
    clearResult(resultKey)
  }, [clearResult, resultKey])

  const onConstructorChange = useCallback((constructorId: string) => {
    setSelectedConstructorId(constructorId)
    setConstructorValueMap(EMPTY_VALUE_MAP)
  }, [])

  const onConstructorValueChange = useCallback((parameter: ParameterData, value: string | number) => {
    setConstructorValueMap((prev) => ({ ...prev, [parameter.name]: value }))
  }, [])

  return {
    isTryItMode,
    valueMap,
    code,
    result,
    executionLogs,
    selectedTechnology,
    snippetTechnology,
    variableCode,
    constructors,
    shouldShowConstructorSelector,
    selectedConstructorId,
    constructorValueMap,
    handleValueChange,
    handleTryIt,
    handleCopy,
    handleRun,
    handleClear,
    onConstructorChange,
    onConstructorValueChange,
  }
}
